import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Toolbar, Container, useTheme, useMediaQuery } from '@mui/material';
import Sidebar from './Sidebar';
import Header from './Header';

// Sidebar takes 16.67% of screen width on desktop (240px on 1440px screen)
// Adjusts proportionally on different screen sizes
const DRAWER_WIDTH_PERCENTAGE = 16.67; // ~16.67% of viewport width
const DRAWER_MIN_WIDTH = 200;
const DRAWER_MAX_WIDTH = 280;

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Calculate responsive drawer width
  const drawerWidth = isMobile 
    ? 280 // Full width on mobile
    : `clamp(${DRAWER_MIN_WIDTH}px, ${DRAWER_WIDTH_PERCENTAGE}vw, ${DRAWER_MAX_WIDTH}px)`;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', overflow: 'hidden' }}>
      <Header onMenuClick={handleDrawerToggle} drawerWidth={drawerWidth} />
      <Sidebar 
        mobileOpen={mobileOpen} 
        onClose={handleDrawerToggle} 
        drawerWidth={drawerWidth}
      />
      
      {/* Main Content Area - Takes remaining percentage */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { 
            xs: '100%', 
            md: `calc(100% - ${typeof drawerWidth === 'string' ? drawerWidth : `${drawerWidth}px`})` 
          },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' 
              ? theme.palette.grey[50] 
              : theme.palette.grey[900],
          transition: 'margin 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          ml: { xs: 0, md: typeof drawerWidth === 'string' ? drawerWidth : `${drawerWidth}px` },
        }}
      >
        {/* Spacer for fixed AppBar */}
        <Toolbar />
        
        {/* Page Content Container - Takes 100% of remaining space */}
        <Container
          maxWidth={false}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            py: { xs: 2, sm: 3, md: 4 },
            px: { xs: 2, sm: 3, md: 4 },
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
